import jsPDF from "jspdf";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import autoTable from "jspdf-autotable";
import {
  convertToGrayScale,
  loadImageDimensions,
  parseAmount,
} from "../modules/invoiceUtils";

export const Generate2InchInvoiceReceipt = async (
   data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
     const {
    documentConfig,hasPayment       
  } = invoiceConfig;
 
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1).length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [49, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = mm(10);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});

  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14; // reset y to top margin for new page
    }
    return y;
  };

   const imagePath =  documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(50); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(12); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

   const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;


  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(7)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += mm(9);
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(6);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  const wrappedStoreTitle = doc.splitTextToSize(storeTitle, pageWidth - mm(20));
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(7);
  });
  doc.setFont("NotoSansMono-Regular", "normal");
  if (documentConfig?.fromData?.phone ||documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph:${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`, pageWidth / 2, y, {
      align: "center",
    });
    y += mm(7);
  }
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);
  const sellerRawAddress = sellerAddressParts.join(", ");
  const wrappedSellerAddress = doc.splitTextToSize(
    sellerRawAddress,
    pageWidth - mm(20),
  );
  wrappedSellerAddress.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(7);
  });
  doc.setFont("NotoSansMono-Regular", "normal");
  if (documentConfig?.fromData?.email) {
    doc.text(`Email: ${documentConfig?.fromData?.email}`, pageWidth / 2, y, {
      align: "center",
    });
    y += mm(7);
  }
  doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
    align: "center",
  });
  y += mm(7);

  doc.setLineDash([mm(1), mm(1)]);
  doc.line(0, y, pageWidth - mm(5), y);
  doc.setLineDash([]);
  y += mm(8);
  const { date, time } = documentConfig?.Date;
  const labelX = 0; // Left-aligned label start
  const colonX = mm(40); // Colon aligned at this point
  const valueX = mm(45); // Value starts after colon
  const lineHeight = mm(10);

  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(7);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(
    data.storeSettings?.saleSettings?.saleModeSettings?.isBill_isTax &&
      data?.salemode?.toUpperCase() === "BILL"
      ? "Bill No"
      : "Invoice No",
    `${data?.saleno}`,
    y,
  );
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Amount", `${documentConfig?.grandTotal}`, y, true);
  y += mm(5);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(0, y, pageWidth - mm(5), y);
  y += mm(10);
  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6.5);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(10);

    doc.setFont("NotoSansMono-Regular", "normal");
    if (buyer?.name) {
      doc.text(`${buyer?.name}`, 0, y);
      y += mm(7);
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, 0, y);
      y += mm(7);
    }
    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);
    const rawAddressText = addressParts.join(", ");
    const wrappedAddress = doc.splitTextToSize(
      rawAddressText,
      pageWidth - mm(20),
    );
    wrappedAddress.forEach((line) => {
      doc.text(line, 0, y);
      y += mm(7);
    });
    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, 0, y);
      y += mm(7);
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, 0, y);
      y += mm(7);
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, 0, y);
      y += mm(7);
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, 0, y);
      y += mm(7);
    }
    if (buyer?.fssai) {
      doc.text(`FSSAI: ${buyer?.cinnumber}`, 0, y);
      y += mm(7);
    }
    if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 0, y);
      y += mm(7);
    }
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(0, y, pageWidth - mm(5), y);
    doc.setLineDash([]);
    y += mm(5);
  }
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 6,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      if (item?.imode === "PRODUCT" && item.note) {
        rows.push([
          {
            content: `(${item?.note})`,
            colSpan: 4,
            styles: {
              fontSize: 5,
              textColor: [100, 100, 100],
              fontStyle: "normal",
            },
          },
        ]);
      }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 6,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: mm(5) },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - mm(5);
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + mm(10);
  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += mm(10);
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += mm(5);
  doc.setDrawColor(0); // light gray
  doc.setLineDash([mm(1), mm(1)]); // dash pattern
  doc.line(0, y, pageWidth - mm(7), y); // draw the line
  doc.setLineDash([]); // reset dash
  y += mm(10);
  // y = checkPageBreak(doc, y);
  doc.setTextColor(0, 0, 0);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += mm(10);
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  doc.setTextColor(0, 0, 0);
  y += mm(5);
  doc.setDrawColor(0); // light gray
  doc.setLineDash([mm(1), mm(1)]); // dash pattern
  doc.line(0, y, pageWidth - mm(7), y); // draw the line
  doc.setLineDash([]);
  y += mm(5);
  // y = checkPageBreak(doc, y);
  const taxHead = isIGST
    ? [["Tax", "Amount", "IGST"]]
    : [["Tax", "Amount", "SGST", "CGST"]];

  const taxBody = Object.entries(taxRows)
    .filter(([percent]) => parseFloat(percent) > 0) // ✅ Skip 0% tax
    .map(([percent, t]) =>
      isIGST
        ? [`${percent}%`, `${t?.basePrice}`, `${t?.igst}`]
        : [`${percent}%`, `${t?.basePrice}`, `${t?.sgst}`, `${t?.cgst}`],
    );
  let tableRendered = false;
  if (taxBody?.length > 0) {
    tableRendered = true;

    autoTable(doc, {
      startY: y,
      head: taxHead.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 6,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      body: taxBody?.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 6,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      theme: "plain",
      styles: {
        font: "NotoSansMono-Regular",
        fontStyle: "normal",
        fontSize: 6,
        cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
        lineWidth: 0,
      },
      margin: { left: 0, right: mm(5) },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
      },
      didDrawCell: (data) => {
        const pageWidth = doc.internal.pageSize.getWidth();

        if (
          data.row.section === "head" &&
          data.row.index === 0 &&
          data.column.index === 0
        ) {
          const top = data.cell.y - mm(2);
          doc.setLineDash([mm(1), mm(1)]);
          doc.setDrawColor(0);
          doc.line(0, top, pageWidth - mm(5), top);
        }

        const isLastTaxRow =
          data.row.section === "body" && data.row.index === taxBody?.length - 1;

        if (isLastTaxRow && data.column.index === 0) {
          const bottom = data.cell.y + data.cell.height + mm(2);
          doc.setLineDash([mm(1), mm(1)]);
          doc.setDrawColor(0);
          doc.line(0, bottom, pageWidth - mm(5), bottom);
          doc.setLineDash([]);
        }
      },
    });
  }
  if (tableRendered && doc.lastAutoTable?.finalY) {
    y = doc.lastAutoTable.finalY + mm(10);
  }
  // y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalTaxAmount) > 0) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
    doc.text("Total Tax", 0, y);
    doc.text(`${documentConfig?.totalTaxAmount}`, pageWidth - mm(5), y, {
      align: "right",
    });
    y += mm(5);
  }

  // -------- PAYMENT MODE HEADER --------
  if (hasPayment) {
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(mm(2), y, pageWidth - mm(2), y);
    doc.setLineDash([]);
    y += mm(10);

    doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
    doc.text("PAYMENT MODE", pageWidth / 2, y, { align: "center" });
    y += mm(8);

    // -------- PAYMENT VALUES --------
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);

    // 2-inch aligned positions
    const payLabelX = mm(2); // label start
    const payColonX = mm(42); // aligned colon
    const payValueX = mm(46); // value after :

    const drawPaymentRow = (label, amount, isBold = false) => {
      if (parseAmount(amount) > 0) {
        // y = checkPageBreak(doc, y);

        doc.setFont(
          isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
          isBold ? "bold" : "normal",
        );
        doc.setFontSize(6);

        doc.text(label, payLabelX, y);
        doc.text(":", payColonX, y);
        doc.text(`${amount}`, payValueX, y);

        y += mm(8);
      }
    };

    // -------- ADJUSTED VALUES --------
    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);

    // -------- DRAW ROWS --------
    drawPaymentRow("Cash", documentConfig?.cashPaid);
    drawPaymentRow("Card", documentConfig?.cardPaid);
    drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
    drawPaymentRow("UPI", documentConfig?.upiPaid);

    // -------- BALANCE DUE (BOLD) --------
    if (documentConfig?.balanceDue > 0) {
      // y += mm(4);
      drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
    }
  }
  // -------- FOOTER LINE --------
  // y += mm(4);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(2), y, pageWidth - mm(2), y);
  doc.setLineDash([]);
  y += mm(7);

  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(7);
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  y += mm(7);
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text("Powered By", pageWidth / 2, y, { align: "center" });
    y += mm(7);
    doc.setFontSize(5);
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += mm(20);
  }
  // setPrint(false);
  return doc;
};

export const Generate2InchBillReceipt = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
    const {
    documentConfig, hasPayment      
  } = invoiceConfig;
 
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items?.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [49, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = mm(10);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData?.statecode !== documentConfig?.toData?.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});

  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14; // reset y to top margin for new page
    }
    return y;
  };

  const imagePath = data?.imagePath;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(50); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(12); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(7)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += mm(9);
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(6);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  const wrappedStoreTitle = doc.splitTextToSize(storeTitle, pageWidth - mm(20));
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(7);
  });
  doc.setFont("NotoSansMono-Regular", "normal");
  if (documentConfig?.fromData?.phone ||documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph:${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`, pageWidth / 2, y, {
      align: "center",
    });
    y += mm(7);
  }
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);
  const sellerRawAddress = sellerAddressParts.join(", ");
  const wrappedSellerAddress = doc.splitTextToSize(
    sellerRawAddress,
    pageWidth - mm(20),
  );
  wrappedSellerAddress.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(7);
  });
  doc.setFont("NotoSansMono-Regular", "normal");
  if (documentConfig?.fromData?.email) {
    doc.text(`Email: ${documentConfig?.fromData?.email}`, pageWidth / 2, y, {
      align: "center",
    });
    y += mm(7);
  }
  // doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
  //   align: "center",
  // });
  // y += mm(7);

  doc.setLineDash([mm(1), mm(1)]);
  doc.line(0, y, pageWidth - mm(5), y);
  doc.setLineDash([]);
  y += mm(8);
  const { date, time } = documentConfig?.Date;
  const labelX = 0; // Left-aligned label start
  const colonX = mm(40); // Colon aligned at this point
  const valueX = mm(45); // Value starts after colon
  const lineHeight = mm(10);

  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(7);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(documentConfig?.numberLabel, `${documentConfig?.number}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Amount", `${documentConfig?.grandTotal}`, y, true);
  y += mm(5);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(0, y, pageWidth - mm(5), y);
  y += mm(10);
  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6.5);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(10);

    doc.setFont("NotoSansMono-Regular", "normal");
    if (buyer?.name) {
      doc.text(`${buyer?.name}`, 0, y);
      y += mm(7);
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, 0, y);
      y += mm(7);
    }
    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);
    const rawAddressText = addressParts.join(", ");
    const wrappedAddress = doc.splitTextToSize(
      rawAddressText,
      pageWidth - mm(20),
    );
    wrappedAddress.forEach((line) => {
      doc.text(line, 0, y);
      y += mm(7);
    });
    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, 0, y);
      y += mm(7);
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, 0, y);
      y += mm(7);
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, 0, y);
      y += mm(7);
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, 0, y);
      y += mm(7);
    }
    if (buyer?.fssai) {
      doc.text(`FSSAI: ${buyer?.cinnumber}`, 0, y);
      y += mm(7);
    }
    if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 0, y);
      y += mm(7);
    }
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(0, y, pageWidth - mm(5), y);
    doc.setLineDash([]);
    y += mm(5);
  }
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items?.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 6,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      if (item?.imode === "PRODUCT" && item?.note) {
        rows.push([
          {
            content: `(${item?.note})`,
            colSpan: 4,
            styles: {
              fontSize: 5,
              textColor: [100, 100, 100],
              fontStyle: "normal",
            },
          },
        ]);
      }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 6,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: mm(5) },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - mm(5);
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + mm(10);
  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += mm(10);
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += mm(5);
  doc.setDrawColor(0); // light gray
  doc.setLineDash([mm(1), mm(1)]); // dash pattern
  doc.line(0, y, pageWidth - mm(7), y); // draw the line
  doc.setLineDash([]); // reset dash
  y += mm(10);
  // y = checkPageBreak(doc, y);
  doc.setTextColor(0, 0, 0);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += mm(10);
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  doc.setTextColor(0, 0, 0);
  y += mm(5);
  // doc.setDrawColor(0); // light gray
  // doc.setLineDash([mm(1), mm(1)]); // dash pattern
  // doc.line(0, y, pageWidth - mm(7), y); // draw the line
  // doc.setLineDash([]);
  // y += mm(5);
  // y = checkPageBreak(doc, y);
  // const taxHead = isIGST
  //   ? [["Tax", "Amount", "IGST"]]
  //   : [["Tax", "Amount", "SGST", "CGST"]];

  // const taxBody = Object.entries(taxRows)
  //   .filter(([percent]) => parseFloat(percent) > 0) // ✅ Skip 0% tax
  //   .map(([percent, t]) =>
  //     isIGST
  //       ? [
  //           `${percent}%`,
  //           `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //         ]
  //       : [
  //           `${percent}%`,
  //           `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //         ],
  //   );
  // let tableRendered = false;
  // if (taxBody.length > 0) {
  //   tableRendered = true;

  //   autoTable(doc, {
  //     startY: y,
  //     head: taxHead.map((row) =>
  //       row.map((cell, colIndex) => ({
  //         content: cell,
  //         styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 6,
  //           halign: colIndex === 0 ? "left" : "right",
  //         },
  //       })),
  //     ),
  //     body: taxBody.map((row) =>
  //       row.map((cell, colIndex) => ({
  //         content: cell,
  //         styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 6,
  //           halign: colIndex === 0 ? "left" : "right",
  //         },
  //       })),
  //     ),
  //     theme: "plain",
  //     styles: {
  //       font: "NotoSansMono-Regular",
  //       fontStyle: "normal",
  //       fontSize: 6,
  //       cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //       lineWidth: 0,
  //     },
  //     margin: { left: 0, right: mm(5) },
  //     columnStyles: {
  //       0: { halign: "left" },
  //       1: { halign: "right" },
  //       2: { halign: "right" },
  //       3: { halign: "right" },
  //     },
  //     didDrawCell: (data) => {
  //       const pageWidth = doc.internal.pageSize.getWidth();

  //       if (
  //         data.row.section === "head" &&
  //         data.row.index === 0 &&
  //         data.column.index === 0
  //       ) {
  //         const top = data.cell.y - mm(2);
  //         doc.setLineDash([mm(1), mm(1)]);
  //         doc.setDrawColor(0);
  //         doc.line(0, top, pageWidth - mm(5), top);
  //       }

  //       const isLastTaxRow =
  //         data.row.section === "body" &&
  //         data.row.index === taxBody.length - 1;

  //       if (isLastTaxRow && data.column.index === 0) {
  //         const bottom = data.cell.y + data.cell.height + mm(2);
  //         doc.setLineDash([mm(1), mm(1)]);
  //         doc.setDrawColor(0);
  //         doc.line(0, bottom, pageWidth - mm(5), bottom);
  //         doc.setLineDash([]);
  //       }
  //     },
  //   });
  // }
  // if (tableRendered && doc.lastAutoTable?.finalY) {
  //   y = doc.lastAutoTable.finalY + mm(10);
  // }
  // y = checkPageBreak(doc, y);
  // if (parseFloat(documentConfig?.totalTaxAmount) > 0) {
  //   doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  //   doc.text("Total Tax", 0, y);
  //   doc.text(
  //     `${CurrencyConverter.toCurrency(documentConfig?.totalTaxAmount || 0, "INR")}`,
  //     pageWidth - mm(5),
  //     y,
  //     { align: "right" },
  //   );
  //   y += mm(5);
  // }

  // -------- PAYMENT MODE HEADER --------
  if (hasPayment) {
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(mm(2), y, pageWidth - mm(2), y);
    doc.setLineDash([]);
    y += mm(10);

    doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
    doc.text("PAYMENT MODE", pageWidth / 2, y, { align: "center" });
    y += mm(8);

    // -------- PAYMENT VALUES --------
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);

    // 2-inch aligned positions
    const payLabelX = mm(2); // label start
    const payColonX = mm(42); // aligned colon
    const payValueX = mm(46); // value after :

    const drawPaymentRow = (label, amount, isBold = false) => {
      if (parseAmount(amount) > 0) {
        // y = checkPageBreak(doc, y);

        doc.setFont(
          isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
          isBold ? "bold" : "normal",
        );
        doc.setFontSize(6);

        doc.text(label, payLabelX, y);
        doc.text(":", payColonX, y);
        doc.text(`${amount}`, payValueX, y);

        y += mm(8);
      }
    };

    // -------- ADJUSTED VALUES --------
    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);

    // -------- DRAW ROWS --------
    drawPaymentRow("Cash", data?.cashPaid);
    drawPaymentRow("Card", data?.cardPaid);
    drawPaymentRow("Online Pay", data?.onlinePaid);
    drawPaymentRow("UPI", data?.upiPaid);

    // -------- BALANCE DUE (BOLD) --------
    if (parseAmount(documentConfig?.balanceDue) > 0) {
      // y += mm(4);
      drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
    }
  }
  // -------- FOOTER LINE --------
  // y += mm(4);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(2), y, pageWidth - mm(2), y);
  doc.setLineDash([]);
  y += mm(7);

  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(7);
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  y += mm(7);
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text("Powered By", pageWidth / 2, y, { align: "center" });
    y += mm(7);
    doc.setFontSize(5);
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += mm(20);
  }
  // setPrint(false);
  return doc;
};

export const Generate3InchInvoiceReceipt = async (
   data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
      const {
    documentConfig, hasPayment      
  } = invoiceConfig;
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items?.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;
  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [74, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = mm(16);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});

  const checkPageBreak = (doc, y, buffer = mm(20)) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return mm(14); // reset y to top margin for new page
    }
    return y;
  };

   const imagePath =  documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(65); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(10); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;


  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(10)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += mm(13);

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(11);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  const wrappedStoreTitle = doc.splitTextToSize(storeTitle, pageWidth - mm(20));
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(10);
  });

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  if (documentConfig?.fromData?.phone ||documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph:${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`, pageWidth / 2, y, {
      align: "center",
    });
    y += mm(8);
  }

  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);

  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - mm(20))
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(8);
    });

  if (seller?.email) {
    doc.text(`Email: ${seller?.email}`, pageWidth / 2, y, { align: "center" });
    y += mm(8);
  }
  doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
    align: "center",
  });
  y += mm(6);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(5), y, pageWidth - mm(5), y);
  doc.setLineDash([]);
  y += mm(12);

  const { date, time } = documentConfig?.Date;
  const labelX = mm(5); // Left-aligned label start
  const colonX = mm(65); // Colon aligned at this point
  const valueX = mm(70); // Value starts after colon
  const lineHeight = mm(8);
  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(8);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(
     documentConfig?.numberLabel ,
    `${documentConfig?.number}`,
    y,
  );
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Amount", `${documentConfig?.grandTotal}`, y, true);
  y += mm(6);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(5), y, pageWidth - mm(5), y);
  y += mm(11);

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(9);

    if (buyer?.name) {
      doc.text(`${buyer?.name}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, mm(10), y);
      y += mm(8);
    }

    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);

    doc
      .splitTextToSize(addressParts.join(", "), pageWidth - mm(20))
      .forEach((line) => {
        doc.text(line, mm(10), y);
        y += mm(8);
      });

    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.fssai) {
      doc.text(`FSSAI: ${buyer?.cinnumber}`, 0, y);
      y += mm(8);
    }
    if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 0, y);
      y += mm(5);
    }
    doc.setDrawColor(0);
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(mm(5), y, pageWidth - mm(5), y);
    doc.setLineDash([]);
    y += mm(4);
  }
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item.title,
          colSpan: 4,
          styles: {
            fontSize: 8,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      // if (item.imode === "PRODUCT" && item.note) {
      //   rows.push([
      //     {
      //       content: `(${item.note})`,
      //       colSpan: 4,
      //       styles: {
      //         fontSize: 5,
      //         textColor: [100, 100, 100],
      //         fontStyle: "normal",
      //       },
      //     },
      //   ]);
      // }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 8,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: mm(5) },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - mm(5);
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + mm(10);
  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += mm(10);
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += mm(5);
  // const itemRows = [
  //   [
  //     {
  //       content: "",
  //       colSpan: 4,
  //       styles: {
  //         fontSize: 9,
  //         // cellPadding: 2,
  //         lineWidth: 0,
  //         halign: "center",
  //       },
  //     },
  //   ],
  //   ...data.saleitems.flatMap((item) => {
  //     const rows = [];

  //     // Title + Note in single line
  //     let titleWithNote = item.title;
  //     if (item.imode === "PRODUCT" && item.note) {
  //       titleWithNote += ` (${item.note})`;
  //     }

  //     rows.push([
  //       {
  //         content: titleWithNote,
  //         colSpan: 4,
  //       },
  //     ]);

  //     // Price row
  //     rows.push([
  //       "",
  //       `${CurrencyConverter.toCurrency(item.soldprice || 0, "INR")}`,
  //       `${item.quantity}`,
  //       `${CurrencyConverter.toCurrency(
  //         item.lineTotalSoldPrice || 0,
  //         "INR",
  //       )}`,
  //     ]);

  //     return rows;
  //   }),
  // ];

  // autoTable(doc, {
  //   startY: y,
  //   pageBreak: "auto",
  //   styles:{
  //     cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //   },
  //   head: [
  //     [
  //       {
  //         content: "Item",
  //         colSpan: 4,
  //         styles: { halign: "left", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //     [
  //       { content: "", styles: { cellPadding: 0 } },
  //       {
  //         content: "Rate",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Qty",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Total",
  //         styles: { halign: "right", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //   ],
  //   body: itemRows,
  //   theme: "plain",
  //   styles: {
  //     fontSize: 12,
  //     styles:{
  //       cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //     },
  //     lineWidth: 0,
  //     font: "NotoSansMono-Regular",
  //     fontStyle: "normal",
  //   },
  //   columnStyles: {
  //     0: { halign: "left" },
  //     1: { halign: "center" },
  //     2: { halign: "center" },
  //     3: { halign: "right" },
  //   },
  //   margin: { left: mm(5), right: mm(5) },
  //   didDrawCell: (data) => {
  //     const pageWidth = doc.internal.pageSize.getWidth();
  //     const left = mm(5);
  //     const right = pageWidth - mm(5);
  //     // Bottom dotted line after the second header row ("Rate Qty Total")
  //     if (
  //       data.row.section === "head" &&
  //       data.row.index === 1 &&
  //       data.column.index === 3
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setDrawColor(0);
  //       doc.setLineDash([mm(1), mm(1)]);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //     // Bottom dotted line after last row
  //     if (
  //       data.row.section === "body" &&
  //       data.row.index === itemRows.length - 1 &&
  //       data.column.index === 0
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + mm(2);
  //       doc.setDrawColor(0);
  //       doc.setLineDash([mm(1), mm(1)]);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //   },
  // });

  // y = doc.lastAutoTable.finalY + mm(20);
  // // y = checkPageBreak(doc, y);
  // doc.setFont("NotoSansMono-Regular", "normal");
  // doc.setFontSize(10);
  // doc.text(`Total Item: ${data.totalItems}`, mm(5), y);
  // y += mm(15);

  // doc.setFont("NotoSansMono-Bold", "bold").setFontSize(14);
  // doc.text(
  //   `Total Amount: ${CurrencyConverter.toCurrency(
  //     documentConfig?.grandTotal || 0,
  //     "INR",
  //   )}.00`,
  //   mm(5),
  //   y,
  // );
  // y += mm(12);

  doc.setLineDash([mm(1), mm(1)]);
  doc.setDrawColor(0);
  doc.line(mm(7), y, pageWidth - mm(7), y);
  doc.setLineDash([]);
  y += mm(11);

  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += mm(10);
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  y += mm(10);
  // y = checkPageBreak(doc, y);
  // TAX SUMMARY
  const taxHead = isIGST
    ? [["Tax", "Amount", "IGST"]]
    : [["Tax", "Amount", "SGST", "CGST"]];

  const taxBody = Object.entries(taxRows)
    .filter(([percent]) => parseFloat(percent) > 0)
    .map(([percent, t]) =>
      isIGST
        ? [`${percent}%`, `${t?.basePrice}`, `${t?.igst}`]
        : [`${percent}%`, `${t?.basePrice}`, `${t?.sgst}`, `${t?.cgst}`],
    );

  if (taxBody.length > 0) {
    autoTable(doc, {
      startY: y,
      pageBreak: "auto",
      head: [
        taxHead[0].map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 8,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ],
      body: taxBody.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 8,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      theme: "plain",
      styles: {
        font: "NotoSansMono-Regular",
        fontStyle: "normal",
        fontSize: 8,
        cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
        lineWidth: 0,
      },
      margin: { left: mm(5), right: mm(5) },
      didDrawCell: (data) => {
        const pageWidth = doc.internal.pageSize.getWidth();

        if (
          data.row.section === "head" &&
          data.row.index === 0 &&
          data.column.index === 0
        ) {
          const top = data.cell.y - 2;
          doc.setLineDash([mm(1), mm(1)]);
          doc.setDrawColor(0);
          doc.line(mm(5), top, pageWidth - mm(5), top);
        }

        const isLastTaxRow =
          data.row.section === "body" && data.row.index === taxBody?.length - 1;

        if (isLastTaxRow && data.column.index === 0) {
          const bottom = data.cell.y + data.cell.height + mm(2);
          doc.setLineDash([mm(1), mm(1)]);
          doc.setDrawColor(0);
          doc.line(mm(5), bottom, pageWidth - mm(5), bottom);
          doc.setLineDash([]);
        }
      },
    });
    y = doc.lastAutoTable.finalY + mm(13);
  }
  // y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalTaxAmount) > 0) {
    doc.setFontSize(9);
    doc.text("Total Tax", mm(5), y);
    doc.text(`${documentConfig?.totalTaxAmount}`, pageWidth - mm(5), y, {
      align: "right",
    });
    y += mm(8);
  }
  // y = checkPageBreak(doc, y);

  const payLabelX = mm(8); // left label
  const payColonX = mm(72); // aligned colon
  const payValueX = mm(78); // value starts just after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      // y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(9);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${amount}`, payValueX, y);

      y += mm(9);
    }
  };

  // -------- PAYMENT MODE HEADER --------
  if (hasPayment) {
    doc.setDrawColor(0);
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(mm(5), y, pageWidth - mm(5), y);
    doc.setLineDash([]);
    y += mm(13);

    doc.setFont("NotoSansMono-Bold", "bold").setFontSize(8);
    doc.text("PAYMENT MODE", pageWidth / 2, y, { align: "center" });
    y += mm(14);

    // -------- PAYMENT VALUES --------
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);

    // Adjust values
    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);

    // Draw rows only if > 0
    drawPaymentRow("Cash", documentConfig?.cashPaid);
    drawPaymentRow("Card", documentConfig?.cardPaid);
    drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
    drawPaymentRow("UPI", documentConfig?.upiPaid);

    // -------- BALANCE DUE --------
    if (parseAmount(documentConfig?.balanceDue) > 0) {
      // y += 6;
      drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
    }
  }
  // -------- FOOTER LINE --------
  y -= mm(4);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(5), y, pageWidth - mm(5), y);
  doc.setLineDash([]);
  y += mm(12);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(12);
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  if (storeSettings?.negobillBranding) {
    y += mm(11);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text("Powered By", pageWidth / 2, y, { align: "center" });
    y += mm(11);
    doc.setFontSize(8);
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;

    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += mm(20);
  }
  // setPrint(false);
  return doc;
};

export const Generate3InchBillReceipt = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
      const {
    documentConfig,hasPayment       
  } = invoiceConfig;
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items?.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;
  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [74, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = mm(16);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});

  const checkPageBreak = (doc, y, buffer = mm(20)) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return mm(14); // reset y to top margin for new page
    }
    return y;
  };

   const imagePath =  documentConfig?.image;
  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(50); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(20); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(10)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += mm(13);

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(11);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  const wrappedStoreTitle = doc.splitTextToSize(storeTitle, pageWidth - mm(20));
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(10);
  });

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  if (documentConfig?.fromData?.phone ||documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph:${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`, pageWidth / 2, y, {
      align: "center",
    });
    y += mm(8);
  }

  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);

  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - mm(20))
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(8);
    });

  if (seller?.email) {
    doc.text(`Email: ${seller?.email}`, pageWidth / 2, y, { align: "center" });
    y += mm(8);
  }
  // doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
  //   align: "center",
  // });
  // y += mm(6);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(5), y, pageWidth - mm(5), y);
  doc.setLineDash([]);
  y += mm(12);

  const { date, time } = documentConfig?.Date;
  const labelX = mm(5); // Left-aligned label start
  const colonX = mm(65); // Colon aligned at this point
  const valueX = mm(70); // Value starts after colon
  const lineHeight = mm(8);
  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(8);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(documentConfig?.numberLabel, `${documentConfig?.number}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Amount", `${documentConfig?.grandTotal}`, y, true);
  y += mm(6);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(5), y, pageWidth - mm(5), y);
  y += mm(11);

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(9);

    if (buyer?.name) {
      doc.text(`${buyer?.name}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, mm(10), y);
      y += mm(8);
    }

    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);

    doc
      .splitTextToSize(addressParts.join(", "), pageWidth - mm(20))
      .forEach((line) => {
        doc.text(line, mm(10), y);
        y += mm(8);
      });

    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, mm(10), y);
      y += mm(8);
    }
    if (buyer?.fssai) {
      doc.text(`FSSAI: ${buyer?.cinnumber}`, 0, y);
      y += mm(8);
    }
    if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 0, y);
      y += mm(5);
    }
    doc.setDrawColor(0);
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(mm(5), y, pageWidth - mm(5), y);
    doc.setLineDash([]);
    y += mm(4);
  }
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 8,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      // if (item.imode === "PRODUCT" && item.note) {
      //   rows.push([
      //     {
      //       content: `(${item.note})`,
      //       colSpan: 4,
      //       styles: {
      //         fontSize: 5,
      //         textColor: [100, 100, 100],
      //         fontStyle: "normal",
      //       },
      //     },
      //   ]);
      // }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 8,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: mm(5) },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - mm(5);
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + mm(10);
  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += mm(10);
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += mm(5);
  // const itemRows = [
  //   [
  //     {
  //       content: "",
  //       colSpan: 4,
  //       styles: {
  //         fontSize: 9,
  //         // cellPadding: 2,
  //         lineWidth: 0,
  //         halign: "center",
  //       },
  //     },
  //   ],
  //   ...data.saleitems.flatMap((item) => {
  //     const rows = [];

  //     // Title + Note in single line
  //     let titleWithNote = item.title;
  //     if (item.imode === "PRODUCT" && item.note) {
  //       titleWithNote += ` (${item.note})`;
  //     }

  //     rows.push([
  //       {
  //         content: titleWithNote,
  //         colSpan: 4,
  //       },
  //     ]);

  //     // Price row
  //     rows.push([
  //       "",
  //       `${CurrencyConverter.toCurrency(item.soldprice || 0, "INR")}`,
  //       `${item.quantity}`,
  //       `${CurrencyConverter.toCurrency(
  //         item.lineTotalSoldPrice || 0,
  //         "INR",
  //       )}`,
  //     ]);

  //     return rows;
  //   }),
  // ];

  // autoTable(doc, {
  //   startY: y,
  //   pageBreak: "auto",
  //   styles:{
  //     cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //   },
  //   head: [
  //     [
  //       {
  //         content: "Item",
  //         colSpan: 4,
  //         styles: { halign: "left", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //     [
  //       { content: "", styles: { cellPadding: 0 } },
  //       {
  //         content: "Rate",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Qty",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Total",
  //         styles: { halign: "right", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //   ],
  //   body: itemRows,
  //   theme: "plain",
  //   styles: {
  //     fontSize: 12,
  //     styles:{
  //       cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //     },
  //     lineWidth: 0,
  //     font: "NotoSansMono-Regular",
  //     fontStyle: "normal",
  //   },
  //   columnStyles: {
  //     0: { halign: "left" },
  //     1: { halign: "center" },
  //     2: { halign: "center" },
  //     3: { halign: "right" },
  //   },
  //   margin: { left: mm(5), right: mm(5) },
  //   didDrawCell: (data) => {
  //     const pageWidth = doc.internal.pageSize.getWidth();
  //     const left = mm(5);
  //     const right = pageWidth - mm(5);
  //     // Bottom dotted line after the second header row ("Rate Qty Total")
  //     if (
  //       data.row.section === "head" &&
  //       data.row.index === 1 &&
  //       data.column.index === 3
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setDrawColor(0);
  //       doc.setLineDash([mm(1), mm(1)]);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //     // Bottom dotted line after last row
  //     if (
  //       data.row.section === "body" &&
  //       data.row.index === itemRows.length - 1 &&
  //       data.column.index === 0
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + mm(2);
  //       doc.setDrawColor(0);
  //       doc.setLineDash([mm(1), mm(1)]);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //   },
  // });

  // y = doc.lastAutoTable.finalY + mm(20);
  // // y = checkPageBreak(doc, y);
  // doc.setFont("NotoSansMono-Regular", "normal");
  // doc.setFontSize(10);
  // doc.text(`Total Item: ${data.totalItems}`, mm(5), y);
  // y += mm(15);

  // doc.setFont("NotoSansMono-Bold", "bold").setFontSize(14);
  // doc.text(
  //   `Total Amount: ${CurrencyConverter.toCurrency(
  //     documentConfig?.grandTotal || 0,
  //     "INR",
  //   )}.00`,
  //   mm(5),
  //   y,
  // );
  // y += mm(12);

  doc.setLineDash([mm(1), mm(1)]);
  doc.setDrawColor(0);
  doc.line(mm(7), y, pageWidth - mm(7), y);
  doc.setLineDash([]);
  y += mm(11);

  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += mm(10);
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  y += mm(6);
  // y = checkPageBreak(doc, y);
  // TAX SUMMARY
  // const taxHead = isIGST
  //   ? [["Tax", "Amount", "IGST"]]
  //   : [["Tax", "Amount", "SGST", "CGST"]];

  // const taxBody = Object.entries(taxRows)
  //   .filter(([percent]) => parseFloat(percent) > 0)
  //   .map(([percent, t]) =>
  //     isIGST
  //       ? [
  //           `${percent}%`,
  //           `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //         ]
  //       : [
  //           `${percent}%`,
  //           `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //           `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //         ],
  //   );

  // if (taxBody.length > 0) {
  //   autoTable(doc, {
  //     startY: y,
  //     pageBreak: "auto",
  //     head: [
  //       taxHead[0].map((cell, colIndex) => ({
  //         content: cell,
  //         styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 8,
  //           halign: colIndex === 0 ? "left" : "right",
  //         },
  //       })),
  //     ],
  //     body: taxBody.map((row) =>
  //       row.map((cell, colIndex) => ({
  //         content: cell,
  //         styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 8,
  //           halign: colIndex === 0 ? "left" : "right",
  //         },
  //       })),
  //     ),
  //     theme: "plain",
  //     styles: {
  //       font: "NotoSansMono-Regular",
  //       fontStyle: "normal",
  //       fontSize: 8,
  //       cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //       lineWidth: 0,
  //     },
  //     margin: { left: mm(5), right: mm(5) },
  //     didDrawCell: (data) => {
  //       const pageWidth = doc.internal.pageSize.getWidth();

  //       if (
  //         data.row.section === "head" &&
  //         data.row.index === 0 &&
  //         data.column.index === 0
  //       ) {
  //         const top = data.cell.y - 2;
  //        doc.setLineDash([mm(1), mm(1)]);
  //         doc.setDrawColor(0);
  //         doc.line(mm(5), top, pageWidth - mm(5), top);
  //       }

  //       const isLastTaxRow =
  //         data.row.section === "body" &&
  //         data.row.index === taxBody.length - 1;

  //       if (isLastTaxRow && data.column.index === 0) {
  //         const bottom = data.cell.y + data.cell.height + mm(2);
  //        doc.setLineDash([mm(1), mm(1)]);
  //         doc.setDrawColor(0);
  //         doc.line(mm(5), bottom, pageWidth - mm(5), bottom);
  //         doc.setLineDash([]);
  //       }
  //     },
  //   });
  //   y = doc.lastAutoTable.finalY + mm(13);
  // }
  // // y = checkPageBreak(doc, y);
  // if (parseFloat(documentConfig?.totalTaxAmount) > 0) {
  //   doc.setFontSize(9);
  //   doc.text("Total Tax", mm(5), y);
  //   doc.text(
  //     `${CurrencyConverter.toCurrency(documentConfig?.totalTaxAmount || 0, "INR")}`,
  //     pageWidth - mm(5),
  //     y,
  //     { align: "right" },
  //   );
  //   y += mm(8);
  // }
  // y = checkPageBreak(doc, y);

  const payLabelX = mm(8); // left label
  const payColonX = mm(72); // aligned colon
  const payValueX = mm(78); // value starts just after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      // y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(9);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${amount}`, payValueX, y);

      y += mm(9);
    }
  };

  // -------- PAYMENT MODE HEADER --------
  if (hasPayment) {
    doc.setDrawColor(0);
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(mm(5), y, pageWidth - mm(5), y);
    doc.setLineDash([]);
    y += mm(13);

    doc.setFont("NotoSansMono-Bold", "bold").setFontSize(8);
    doc.text("PAYMENT MODE", pageWidth / 2, y, { align: "center" });
    y += mm(14);

    // -------- PAYMENT VALUES --------
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);

    // Adjust values
    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);

    // Draw rows only if > 0
    drawPaymentRow("Cash", data?.cashPaid);
    drawPaymentRow("Card", data?.cardPaid);
    drawPaymentRow("Online Pay", data?.onlinePaid);
    drawPaymentRow("UPI", data?.upiPaid);

    // -------- BALANCE DUE --------
    if (parseAmount(documentConfig?.balanceDue) > 0) {
      // y += 6;
      drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
    }
  }
  // -------- FOOTER LINE --------
  y -= mm(4);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(5), y, pageWidth - mm(5), y);
  doc.setLineDash([]);
  y += mm(12);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(12);
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  if (storeSettings?.negobillBranding) {
    y += mm(11);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text("Powered By", pageWidth / 2, y, { align: "center" });
    y += mm(11);
    doc.setFontSize(8);
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;

    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += mm(20);
  }
  // setPrint(false);
  return doc;
};

export const Generate4InchInvoiceReceipt = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
   const {
    documentConfig, hasPayment      
  } = invoiceConfig
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items?.length * mm(65); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(55);
  const footerHeight = mm(350); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(350); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [101.6, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = mm(18);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14;
    }
    return y;
  };

    const imagePath =  documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(80); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(20); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  // Header
  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;


  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(11)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += mm(13);

  doc.setFontSize(13);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - mm(30)).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(13);
  });

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  if (documentConfig?.fromData?.phone ||documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph: ${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`, pageWidth / 2, y, {
      align: "center",
    });
    y += mm(9);
  }

  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);

  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - 30)
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(9);
    });

  if (seller?.email) {
    doc.text(`Email: ${seller?.email}`, pageWidth / 2, y, { align: "center" });
    y += mm(9);
  }
  doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
    align: "center",
  });
  y += mm(5);

  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  doc.setLineDash([]);
  y += mm(10);

  const { date, time } = documentConfig?.Date;
  const labelX = mm(8); // Left-aligned label start
  const colonX = mm(110); // Colon aligned at this point
  const valueX = mm(120); // Value starts after colon
  const lineHeight = mm(10);
  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(9);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(
     documentConfig?.numberLabel ,
    `${documentConfig?.number}`,
    y,
  );
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Total Amount", `${documentConfig?.grandTotal}`, y, true);
  y += mm(7);

  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  y += mm(10);

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(11);

    if (buyer?.name) {
      doc.text(`${buyer?.name}`, mm(10), y);
      y += mm(11);
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, mm(10), y);
      y += mm(11);
    }

    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);

    doc
      .splitTextToSize(addressParts.join(", "), pageWidth - mm(20))
      .forEach((line) => {
        doc.text(line, mm(11), y);
        y += mm(11);
      });

    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, mm(10), y);
      y += mm(11);
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, mm(10), y);
      y += mm(11);
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, mm(10), y);
      y += mm(11);
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, mm(10), y);
      y += mm(11);
    }
    if (buyer?.fssai) {
      doc.text(`FSSAI: ${buyer?.cinnumber}`, 0, y);
      y += mm(11);
    }
    if (buyer?.referenceid) {
      doc.text(`Reference No: ${buyer?.referenceid}`, 0, y);
      y += mm(11);
    }
  }
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  doc.setLineDash([]);
  y += mm(9);
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 9,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      // if (item.imode === "PRODUCT" && item.note) {
      //   rows.push([
      //     {
      //       content: `(${item.note})`,
      //       colSpan: 4,
      //       styles: {
      //         fontSize: 5,
      //         textColor: [100, 100, 100],
      //         fontStyle: "normal",
      //       },
      //     },
      //   ]);
      // }

      // Price row
      rows.push([
        "",
        `${item.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 9,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: mm(5) },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - mm(5);
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + mm(11);
  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += mm(10);
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(10);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += mm(5);
  // const itemRows = [
  //   [
  //     {
  //       content: "",
  //       colSpan: 4,
  //       styles: {
  //         fontSize: 9,
  //         cellPadding: 2,
  //         lineWidth: 0,
  //         halign: "center",
  //       },
  //     },
  //   ],
  //   ...data.saleitems.flatMap((item) => {
  //     const rows = [];

  //     // Title + Note in single line
  //     let titleWithNote = item.title;
  //     // if (item.imode === "PRODUCT" && item.note) {
  //     //   titleWithNote += ` (${item.note})`;
  //     // }

  //     rows.push([
  //       {
  //         content: titleWithNote,
  //         colSpan: 4,
  //       },
  //     ]);

  //     // Price row
  //     rows.push([
  //       "",
  //       `${CurrencyConverter.toCurrency(item.soldprice || 0, "INR")}`,
  //       `${item.quantity}`,
  //       `${CurrencyConverter.toCurrency(
  //         item.lineTotalSoldPrice || 0,
  //         "INR",
  //       )}`,
  //     ]);

  //     return rows;
  //   }),
  // ];
  // autoTable(doc, {
  //   startY: y,
  //   head: [
  //     [
  //       {
  //         content: "Item",
  //         colSpan: 4,
  //         styles: { halign: "left", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //     [
  //       { content: "", styles: { cellPadding: 0 } },
  //       {
  //         content: "Rate",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Qty",
  //         styles: { halign: "center", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //       {
  //         content: "Total",
  //         styles: { halign: "right", fontStyle: "bold", font: "NotoSansMono-Bold" },
  //       },
  //     ],
  //   ],
  //   body: itemRows,
  //   theme: "plain",
  //   styles: {
  //     fontSize: 14,
  //     cellPadding: 3,
  //     lineWidth: 0,
  //     font: "NotoSansMono-Regular",
  //   },
  //   columnStyles: {
  //     0: { halign: "left" },
  //     1: { halign: "center" },
  //     2: { halign: "center" },
  //     3: { halign: "right" },
  //   },
  //   margin: { left: 8, right: 8 },
  //   didDrawCell: (data) => {
  //     const pageWidth = doc.internal.pageSize.getWidth();
  //     const left = 8;
  //     const right = pageWidth - 8;

  //     if (
  //       data.row.section === "head" &&
  //       data.row.index === 1 &&
  //       data.column.index === 3
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setLineDash([1, 1], 0);
  //       doc.setDrawColor(0);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //     if (
  //       data.row.section === "body" &&
  //       data.row.index === itemRows.length - 1 &&
  //       data.column.index === 0
  //     ) {
  //       const bottom = data.cell.y + data.cell.height + 2;
  //       doc.setLineDash([1, 1], 0);
  //       doc.setDrawColor(0);
  //       doc.line(left, bottom, right, bottom);
  //       doc.setLineDash([]);
  //     }
  //   },
  // });

  // y = doc.lastAutoTable.finalY + 18;
  // // y = checkPageBreak(doc, y);
  // doc.setFontSize(14);
  // doc.text(`Total Item: ${data.totalItems}`, 8, y);
  // y += 24;
  // doc.setFont("NotoSansMono-Bold", "bold").setFontSize(18);
  // doc.text(
  //   `Total Amount: ${CurrencyConverter.toCurrency(
  //     documentConfig?.grandTotal || 0,
  //     "INR",
  //   )}.00`,
  //   8,
  //   y,
  // );
  // y += 12;

  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  y += mm(10);
  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += mm(10);
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  y += mm(11);
  // y = checkPageBreak(doc, y);
  const taxHead = isIGST
    ? [["Tax", "Amount", "IGST"]]
    : [["Tax", "Amount", "SGST", "CGST"]];

  const taxBody = Object.entries(taxRows)
    .filter(([percent]) => parseFloat(percent) > 0)
    .map(([percent, t]) =>
      isIGST
        ? [`${percent}%`, `${t?.basePrice}`, `${t?.igst}`]
        : [`${percent}%`, `${t?.basePrice}`, `${t?.sgst}`, `${t?.cgst}`],
    );

  if (taxBody.length > 0) {
    autoTable(doc, {
      startY: y,
      pageBreak: "auto",
      head: [
        taxHead[0].map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 9,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ],
      body: taxBody?.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 9,
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      theme: "plain",
      styles: {
        font: "NotoSansMono-Regular",
        fontStyle: "normal",
        fontSize: 9,
        cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
        lineWidth: 0,
      },
      margin: { left: mm(5), right: mm(5) },
      didDrawCell: (data) => {
        const pageWidth = doc.internal.pageSize.getWidth();

        if (
          data.row.section === "head" &&
          data.row.index === 0 &&
          data.column.index === 0
        ) {
          const top = data.cell.y - 2;
          doc.setLineDash([mm(1), mm(1)]);
          doc.setDrawColor(0);
          doc.line(mm(5), top, pageWidth - mm(5), top);
        }

        const isLastTaxRow =
          data.row.section === "body" && data.row.index === taxBody?.length - 1;

        if (isLastTaxRow && data.column.index === 0) {
          const bottom = data.cell.y + data.cell.height + mm(2);
          doc.setLineDash([mm(1), mm(1)]);
          doc.setDrawColor(0);
          doc.line(mm(5), bottom, pageWidth - mm(5), bottom);
          doc.setLineDash([]);
        }
      },
    });
    y = doc.lastAutoTable.finalY + mm(13);
  }
  // y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalTaxAmount) > 0) {
    doc.setFontSize(9);
    doc.text("Total Tax", mm(5), y);
    doc.text(`${documentConfig?.totalTaxAmount}`, pageWidth - mm(5), y, {
      align: "right",
    });
    y += mm(9);
  }
  // y = checkPageBreak(doc, y);
  // Final footer

  // y += mm(20);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);

  const payLabelX = mm(10);
  const payColonX = mm(125); // same column for all :
  const payValueX = mm(132); // amount starts just after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      // y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(9);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${amount}`, payValueX, y);

      y += mm(11);
    }
  };

  // ---------- PAYMENT MODE HEADER ----------
  if (hasPayment) {
    doc.setDrawColor(0);
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(mm(10), y, pageWidth - mm(10), y);
    doc.setLineDash([]);
    y += mm(10);

    doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
    doc.text("PAYMENT MODE", pageWidth / 2, y + mm(1), { align: "center" });
    y += mm(12);

    // ---------- PAYMENT VALUES ----------
    doc.setFont("NotoSansMono-Regular", "normal");

   
    // Draw only if value > 0
    drawPaymentRow("Cash", documentConfig?.cashPaid);
    drawPaymentRow("Card", documentConfig?.cardPaid);
    drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
    drawPaymentRow("UPI", documentConfig?.upiPaid);

    // ---------- BALANCE DUE ----------
    if (parseAmount(documentConfig?.balanceDue) > 0) {
      // y += 6;
      drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
    }
  }
  // ---------- FOOTER LINE ----------
  y -= mm(5);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  doc.setLineDash([]);
  y += mm(10);
  doc.setFontSize(9);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(10);
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  y += mm(10);
  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += mm(10);
  doc.setFontSize(9);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX = (pageWidth - totalWidth) / 2;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += mm(20);
  }
  // setPrint(false);
  return doc;
};

export const Generate4InchBillReceipt = async (
 data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
       const {
    documentConfig,hasPayment       
  } = invoiceConfig;
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items?.length * mm(65); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(55);
  const footerHeight = mm(350); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(350); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [101.6, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = mm(18);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14;
    }
    return y;
  };

   const imagePath = documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(80); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(20); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  // Header
   const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(11)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += mm(13);

  doc.setFontSize(13);
  const storeTitle = documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - mm(30)).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(13);
  });

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  if (documentConfig?.fromData?.phone ||documentConfig?.fromData?.additionalPhone) {
    doc.text(`Ph: ${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`, pageWidth / 2, y, {
      align: "center",
    });
    y += mm(9);
  }

  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);

  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - 30)
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(9);
    });

  if (seller?.email) {
    doc.text(`Email: ${seller?.email}`, pageWidth / 2, y, { align: "center" });
    y += mm(9);
  }
  // doc.text(`GST: ${seller?.gstnumber || ""}`, pageWidth / 2, y, {
  //   align: "center",
  // });
  // y += mm(5);

  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  doc.setLineDash([]);
  y += mm(10);

  const { date, time } = documentConfig?.Date;
  const labelX = mm(8); // Left-aligned label start
  const colonX = mm(110); // Colon aligned at this point
  const valueX = mm(120); // Value starts after colon
  const lineHeight = mm(10);
  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(9);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned(documentConfig?.numberLabel, `${documentConfig?.number}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight;
  drawRowWithColonAligned("Total Amount", `${documentConfig?.grandTotal}`, y, true);
  y += mm(7);

  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  y += mm(10);

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(11);

    if (buyer?.name) {
      doc.text(`${buyer?.name}`, mm(10), y);
      y += mm(11);
    }
    if (buyer?.phone) {
      doc.text(`Ph: ${buyer?.phone}`, mm(10), y);
      y += mm(11);
    }

    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ].filter(Boolean);

    doc
      .splitTextToSize(addressParts.join(", "), pageWidth - mm(20))
      .forEach((line) => {
        doc.text(line, mm(11), y);
        y += mm(11);
      });

    if (buyer?.email) {
      doc.text(`Email: ${buyer?.email}`, mm(10), y);
      y += mm(11);
    }
    if (buyer?.gstnumber) {
      doc.text(`GST: ${buyer?.gstnumber}`, mm(10), y);
      y += mm(11);
    }
    if (buyer?.pannumber) {
      doc.text(`PAN: ${buyer?.pannumber}`, mm(10), y);
      y += mm(11);
    }
    if (buyer?.cinnumber) {
      doc.text(`CIN: ${buyer?.cinnumber}`, mm(10), y);
      y += mm(11);
    }
  }
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  doc.setLineDash([]);
  y += mm(9);
  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    // Actual item rows
    ...documentConfig?.items.flatMap((item) => {
      const rows = [];

      // Title row
      rows.push([
        {
          content: item?.title,
          colSpan: 4,
          styles: {
            fontSize: 9,
            fontStyle: "normal",
            font: "NotoSansMono-Regular",
          },
        },
      ]);

      // Note row (only if present and imode = PRODUCT)
      // if (item.imode === "PRODUCT" && item.note) {
      //   rows.push([
      //     {
      //       content: `(${item.note})`,
      //       colSpan: 4,
      //       styles: {
      //         fontSize: 5,
      //         textColor: [100, 100, 100],
      //         fontStyle: "normal",
      //       },
      //     },
      //   ]);
      // }

      // Price row
      rows.push([
        "",
        `${item?.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item?.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    styles: {
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
    },
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 9,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineWidth: 0,
      font: "NotoSansMono-Regular",
      fontStyle: "normal",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 0, right: mm(5) },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 0;
      const right = pageWidth - mm(5);
      // Bottom dotted line after the second header row ("Rate Qty Total")
      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        // doc.setLineDash([]);
      }
      // Bottom dotted line after last row
      if (
        data.row.section === "body" &&
        data.row.index === itemRows?.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + mm(2);
        doc.setLineDash([mm(1), mm(1)]);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });
  y = doc.lastAutoTable.finalY + mm(11);
  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  doc.text(`Total Item: ${data?.totalItems}`, 0, y);
  y += mm(10);
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(10);
  doc.text(`Grand Total: ${documentConfig?.grandTotal}`, 0, y, { align: "left" });
  y += mm(5);

  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  y += mm(10);
  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += mm(10);
  }
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    doc.text(`${documentConfig?.totalDiscount}`, pageWidth / 2, y, { align: "center" });
  }
  y += mm(6);

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);

  const payLabelX = mm(10);
  const payColonX = mm(125); // same column for all :
  const payValueX = mm(132); // amount starts just after :

  const drawPaymentRow = (label, amount, isBold = false) => {
    if (parseAmount(amount) > 0) {
      // y = checkPageBreak(doc, y);

      doc.setFont(
        isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
        isBold ? "bold" : "normal",
      );
      doc.setFontSize(9);

      doc.text(label, payLabelX, y);
      doc.text(":", payColonX, y);
      doc.text(`${amount}`, payValueX, y);

      y += mm(11);
    }
  };

  // ---------- PAYMENT MODE HEADER ----------
  if (hasPayment) {
    doc.setDrawColor(0);
    doc.setLineDash([mm(1), mm(1)]);
    doc.line(mm(10), y, pageWidth - mm(10), y);
    doc.setLineDash([]);
    y += mm(10);

    doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
    doc.text("PAYMENT MODE", pageWidth / 2, y + mm(1), { align: "center" });
    y += mm(12);

    // ---------- PAYMENT VALUES ----------
    doc.setFont("NotoSansMono-Regular", "normal");

    // Adjusted values
    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);

    // Draw only if value > 0
    drawPaymentRow("Cash", documentConfig?.cashPaid);
    drawPaymentRow("Card", documentConfig?.cardPaid);
    drawPaymentRow("Online Pay", documentConfig?.onlinePaid);
    drawPaymentRow("UPI", documentConfig?.upiPaid);

    // ---------- BALANCE DUE ----------
    if (parseAmount(documentConfig?.balanceDue) > 0) {
      // y += 6;
      drawPaymentRow("Balance Due", documentConfig?.balanceDue, true);
    }
  }
  // ---------- FOOTER LINE ----------
  y -= mm(5);
  doc.setDrawColor(0);
  doc.setLineDash([mm(1), mm(1)]);
  doc.line(mm(10), y, pageWidth - mm(10), y);
  doc.setLineDash([]);
  y += mm(10);
  doc.setFontSize(9);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(10);
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  y += mm(10);
  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += mm(10);
  doc.setFontSize(9);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX = (pageWidth - totalWidth) / 2;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
    y += mm(20);
  }
  // setPrint(false);
  return doc;
};
//FINal


export const Generate4InchBillReceipts = async (
  data,
  storeSettings,
  optData,
) => {
  const saleDate = data?.saledate;
  const timezone = data?.saleby?.timezone;  
  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }
  const doc = new jsPDF({
    unit: "pt",
    format: [288, 1000],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 18;

  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14; // reset y to top margin for new page
    }
    return y;
  };

  const imagePath = data.imagePath;

  if (data?.salefrom?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = 80; // maximum width in pt for 3-inch receipt
      const maxHeight = 40; // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + 20; // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  // Header
  const invoiceTitle =
    data.status === "CANCELLED" ? "CANCELLED BILL RECIEPT" : "BILL RECIEPT";

  doc
    .setFont("NotoSansMono-Bold", "bold")
    .setFontSize(16)
    .text(invoiceTitle, pageWidth / 2, y, { align: "center" });
  y += 18;

  doc.setFontSize(14);
  const storeTitle = data.salefrom?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - 30).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += 16;
  });

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(14);
  if (data.salefrom?.phone) {
    doc.text(`Ph: ${data.salefrom.phone}`, pageWidth / 2, y, {
      align: "center",
    });
    y += 16;
  }

  const seller = data.salefrom;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state, seller?.district, seller?.country]
      .filter(Boolean)
      .join(", "),
    seller?.pincode ? `- ${seller?.pincode}` : null,
  ].filter(Boolean);

  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - 30)
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += 16;
    });

  if (seller?.email) {
    doc.text(`Email: ${seller?.email || ""}`, pageWidth / 2, y, {
      align: "center",
    });
    y += 16;
  }

  doc.setLineDash([1], 0);
  doc.line(8, y, pageWidth - 8, y);
  doc.setLineDash([]);
  y += 16;

  const { date, time } = saleTime;
  const labelX = 8; // Left-aligned label start
  const colonX = 110; // Colon aligned at this point
  const valueX = 120; // Value starts after colon
  const lineHeight = 17;
  const drawRowWithColonAligned = (label, value, yPos, isBold = false) => {
    const font = isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular";
    doc.setFont(font, isBold ? "bold" : "normal").setFontSize(15);
    doc.text(label, labelX, yPos);
    doc.text(":", colonX, yPos);
    doc.text(value, valueX, yPos);
  };
  drawRowWithColonAligned("Bill No", `${data.saleno}`, y);
  y += lineHeight + 2;
  drawRowWithColonAligned("Date", `${date}, ${time}`, y);
  y += lineHeight + 6;
  drawRowWithColonAligned("Total Amount", `${data.grandtotal}`, y, true);
  y += 12;

  doc.setLineDash([1], 0);
  doc.line(8, y, pageWidth - 8, y);
  y += 16;

  const buyer = data.saleto;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  if (hasBuyerInfo && optData?.isCustomerDisplay) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(14);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += 18;

    if (buyer.name) {
      doc.text(`${buyer.name}`, 10, y);
      y += 16;
    }
    if (buyer.phone) {
      doc.text(`Ph: ${buyer.phone}`, 10, y);
      y += 16;
    }

    const addressParts = [
      buyer.addressline1,
      [buyer.state, buyer.district, buyer.country].filter(Boolean).join(", "),
      buyer.pincode ? `- ${buyer.pincode}` : null,
    ].filter(Boolean);

    doc
      .splitTextToSize(addressParts.join(", "), pageWidth - 20)
      .forEach((line) => {
        doc.text(line, 11, y);
        y += 16;
      });

    if (buyer.email) {
      doc.text(`Email: ${buyer.email}`, 10, y);
      y += 16;
    }
    doc.setLineDash([1], 0);
    doc.line(8, y, pageWidth - 8, y);
    doc.setLineDash([]);
    y += 12;
  }

  const itemRows = [
    [
      {
        content: "",
        colSpan: 4,
        styles: {
          fontSize: 14,
          cellPadding: 2,
          lineWidth: 0,
          halign: "center",
        },
      },
    ],
    ...data.saleitems.flatMap((item) => {
      const rows = [];

      // Title + Note in single line
      let titleWithNote = item.title;
      if (item.imode === "PRODUCT" && item.note) {
        titleWithNote += ` (${item.note})`;
      }

      rows.push([
        {
          content: titleWithNote,
          colSpan: 4,
        },
      ]);

      // Price row
      rows.push([
        "",
        `${item.soldprice}`,
        `${item?.quantity} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${item.lineTotalSoldPrice}`,
      ]);

      return rows;
    }),
  ];

  autoTable(doc, {
    startY: y,
    head: [
      [
        {
          content: "Item",
          colSpan: 4,
          styles: {
            halign: "left",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
      [
        { content: "", styles: { cellPadding: 0 } },
        {
          content: "Rate",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Qty",
          styles: {
            halign: "center",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
            fontStyle: "bold",
            font: "NotoSansMono-Bold",
          },
        },
      ],
    ],
    body: itemRows,
    theme: "plain",
    styles: {
      fontSize: 14,
      cellPadding: 3,
      lineWidth: 0,
      font: "NotoSansMono-Regular",
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "right" },
    },
    margin: { left: 8, right: 8 },
    didDrawCell: (data) => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const left = 8;
      const right = pageWidth - 8;

      if (
        data.row.section === "head" &&
        data.row.index === 1 &&
        data.column.index === 3
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1], 0);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
      if (
        data.row.section === "body" &&
        data.row.index === itemRows.length - 1 &&
        data.column.index === 0
      ) {
        const bottom = data.cell.y + data.cell.height + 2;
        doc.setLineDash([1, 1], 0);
        doc.setDrawColor(0);
        doc.line(left, bottom, right, bottom);
        doc.setLineDash([]);
      }
    },
  });

  y = doc.lastAutoTable.finalY + 18;
  // y = checkPageBreak(doc, y);
  doc.setFontSize(14);
  doc.text(`Total Item: ${data.totalItems}`, 8, y);
  y += 24;
  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(18);
  doc.text(`Total Amount: ${data.grandtotal}`, 8, y);
  y += 12;

  doc.setLineDash([1, 1], 0);
  doc.line(10, y, pageWidth - 10, y);
  y += 18;
  // y = checkPageBreak(doc, y);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(14);
  if (parseFloat(data.totaldiscount) > 0) {
    doc.text("**** YOU SAVED ****", pageWidth / 2, y, { align: "center" });
    y += 16;
  }
  if (parseFloat(data.totaldiscount) > 0) {
    doc.text(`${data.totaldiscount}`, pageWidth / 2, y, { align: "center" });
  }
  y += 18;
  // y = checkPageBreak(doc, y);
  doc.setFontSize(14);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += 20;
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  if (data?.negobillBranding) {
    y += 20;
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(14);

    const payLabelX = 10;
    const payColonX = 125; // same column for all :
    const payValueX = 132; // amount starts just after :

    const drawPaymentRow = (label, amount, isBold = false) => {
      if (Number(amount) > 0) {
        // y = checkPageBreak(doc, y);

        doc.setFont(
          isBold ? "NotoSansMono-Bold" : "NotoSansMono-Regular",
          isBold ? "bold" : "normal",
        );
        doc.setFontSize(14);

        doc.text(label, payLabelX, y);
        doc.text(":", payColonX, y);
        doc.text(`${amount}`, payValueX, y);

        y += 14;
      }
    };

    // ---------- PAYMENT MODE HEADER ----------
    if (optData?.isPayment) {
      doc.setLineDash([1, 1], 0);
      doc.line(8, y, pageWidth - 8, y);
      doc.setLineDash([]);
      y += 12;

      doc.setFont("NotoSansMono-Bold", "bold").setFontSize(14);
      doc.text("PAYMENT MODE", pageWidth / 2, y + 1, { align: "center" });
      y += 16;

      // ---------- PAYMENT VALUES ----------
      doc.setFont("NotoSansMono-Regular", "normal");

      // Adjusted values
      const cashPaid = (data.cashamount || 0) - (data.cashchange || 0);

      const cardPaid = (data.cardamount || 0) - (data.cardchange || 0);

      const onlinePaid = (data.onlineamount || 0) - (data.onlinechange || 0);

      const upiPaid = (data.upiamount || 0) - (data.upichange || 0);

      const balanceDue =
        (data.paylateramount || 0) - (data.paylaterchange || 0);

      // Draw only if value > 0
      drawPaymentRow("Cash", data.cashPaid);
      drawPaymentRow("Card", data.cardPaid);
      drawPaymentRow("Online Pay", data.onlinePaid);
      drawPaymentRow("UPI", data.upiPaid);

      // ---------- BALANCE DUE ----------
      if (data.balanceDue > 0) {
        y += 6;
        drawPaymentRow("Balance Due", data.balanceDue, true);
      }
    }
    // ---------- FOOTER LINE ----------
    y += 4;
    doc.setLineDash([1, 1], 0);
    doc.line(8, y, pageWidth - 8, y);
    doc.setLineDash([]);
    y += 16;

    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text("Powered By", pageWidth / 2, y, { align: "center" });
    y += 18;

    doc.setFontSize(10);
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;

    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
  }
  // setPrint(false);
  return doc;
};

export const Generate2InchSimpleReceipt = async (
  data,
  storeSettings,
  settings,
  timezone = "Asia/Kolkata",
) => {
  // setPrint(true);
  const mm = (pt) => pt / 2.83465;
  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [49, mm(1000)],
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = mm(18);

  // --- Helper: Page Break Check ---
  const checkPageBreak = (y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return mm(14);
    }
    return y;
  };

  // --- HEADER TITLE ---
  doc.setFont("Inter-Bold", "bold").setFontSize(10);
  doc.text("RECEIPT", pageWidth / 2, y, { align: "center" });
  y += mm(18);

  // --- STORE NAME ---
  doc.setFont("Inter-Regular", "normal").setFontSize(9);
  const wrappedStoreTitle = doc.splitTextToSize(
    settings?.saleProfileData?.title,
    pageWidth - mm(30),
  );
  wrappedStoreTitle.forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(13);
  });

  doc.setFontSize(8);
  if (settings?.saleProfileData?.phone) {
    doc.text(
      `Ph: ${settings?.saleProfileData?.phone || ""}`,
      pageWidth / 2,
      y,
      { align: "center" },
    );
    y += mm(16);
  }

  // y += mm(16);
  doc.setFontSize(8);

  // --- JOB NO ---
  if (data?.jobNo) {
    doc.setFont("Inter-Bold", "bold");
    doc.text(`Job No:`, mm(10), y);
    doc.setFont("Inter-Regular", "normal");
    doc.text(String(data.jobNo), mm(50), y);
    y += mm(13);
  }

  // --- DATE & TIME ---
  let dateStr = "N/A";
  let timeStr = "N/A";
  if (data?.updatedAt) {
    const converted = DateConverter.convertToTimeZone(data.updatedAt, timezone);
    const formatted = DateConverter.ISOToDisplayFormat(converted);
    dateStr = formatted.date || "-";
    timeStr = formatted.time || "-";
  }

  doc.setFont("Inter-Bold", "bold").setFontSize(8);
  doc.text("Date:", mm(10), y);
  doc.setFont("Inter-Regular", "normal");
  doc.text(`${dateStr} | ${timeStr}`, mm(43), y);
  y += mm(13);

  // --- ADVANCE PAID (moved here below date) ---
  if (data?.totalAdvancePaid && Number(data.totalAdvancePaid) > 0) {
    doc.setFont("Inter-Bold", "bold").setFontSize(8);
    doc.text("Advance Paid:", mm(10), y);
    doc.setFont("Inter-Bold", "bold");
    doc.text(
      `₹${CurrencyConverter.toCurrency(data.totalAdvancePaid || 0, "INR")}.00`,
      mm(70),
      y,
    );
    y += mm(5);
  }

  // --- LINE ---
  doc.setLineDash([mm(1), mm(1)], 0);
  doc.line(mm(8), y, pageWidth - mm(8), y);
  doc.setLineDash([]);
  y += mm(14);

  // --- CUSTOMER DETAILS HEADING (only if name or phone exists) ---
  if (data?.customer?.name || data?.customer?.phone) {
    doc.setFont("Inter-Bold", "bold").setFontSize(9);
    doc.text("Customer Details", mm(10), y);
    y += mm(13);

    // --- CUSTOMER NAME ---
    if (data.customer?.name) {
      doc.setFont("Inter-Bold", "bold").setFontSize(9);
      doc.text("Name:", mm(10), y);
      doc.setFont("Inter-Regular", "normal");
      doc.text(String(data?.customer?.name), mm(45), y);
      y += mm(13);
    }

    // --- CUSTOMER PHONE ---
    if (data.customer?.phone) {
      doc.setFont("Inter-Bold", "bold").setFontSize(9);
      doc.text("Phone:", mm(10), y);
      doc.setFont("Inter-Regular", "normal");
      doc.text(String(data.customer.phone), mm(45), y);
      y += mm(13);
    }

    // --- DOTTED LINE AFTER CUSTOMER DETAILS ---
    doc.setLineDash([mm(1), mm(1)], 0);
    doc.line(mm(8), y, pageWidth - mm(8), y);
    doc.setLineDash([]);
    y += mm(16);
  }

  // --- JOB TITLE (task title) ---
  if (data?.title) {
    doc.setFont("Inter-Bold", "bold").setFontSize(9);
    doc.text("Title:", mm(10), y);
    doc.setFont("Inter-Regular", "normal");
    const wrappedTitle = doc.splitTextToSize(
      String(data.title),
      pageWidth - mm(20),
    );
    wrappedTitle.forEach((line) => {
      doc.text(line, mm(40), y);
      y += mm(13);
    });
    // y += mm(6);
  }
  // --- TASK LIST ---
  // if (Array.isArray(data?.task) && data.task.length > 0) {
  //   const validTasks = data.task.filter(
  //     (item) => item?.status !== "CANCELLED"
  //   );

  //   if (validTasks.length > 0) {
  //     // doc.setFont("Inter-Bold", "bold").setFontSize(11);
  //     // doc.text("Tasks", 10, y);
  //     // y += 14;

  //     // --- HEADER ---
  //     doc.setFont("Inter-Bold", "bold").setFontSize(10);
  //     doc.text("Title", 10, y);
  //     doc.text("Amount", pageWidth - 10, y, { align: "right" });
  //     y += 12;

  //     doc.setLineDash([1, 1], 0);
  //     doc.line(8, y, pageWidth - 8, y);
  //     doc.setLineDash([]);
  //     y += 10;

  //     // --- ITEMS ---
  //     validTasks.forEach((item) => {
  //       y = checkPageBreak(y);

  //       const title = String(item?.title || "");
  //       const amount = `${CurrencyConverter.toCurrency(item?.amount || 0, "INR")}`;

  //       const wrappedTitle = doc.splitTextToSize(title, pageWidth - 80);

  //       wrappedTitle.forEach((line, index) => {
  //         doc.setFont("Inter-Regular", "normal").setFontSize(10);

  //         doc.text(line, 10, y+2);

  //         if (index === 0) {
  //           doc.text(amount, pageWidth - 10, y+2, { align: "right" });
  //         }

  //         y += 12;
  //       });
  //     });

  //     // y += 10;

  //     // --- LINE ---
  //     doc.setLineDash([1, 1], 0);
  //     doc.line(8, y, pageWidth - 8, y);
  //     doc.setLineDash([]);
  //     y += 16;
  //   }
  // }
  if (data?.dueDate) {
    doc.setFont("Inter-Bold", "bold");
    doc.text("Due Date:", mm(10), y);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      `${
        DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(data?.dueDate),
        ).date
      }`,
      mm(59),
      y,
    );
    y += mm(6);
  }
  // y += mm(6);
  // --- LINE ---
  doc.setLineDash([mm(1), mm(1)], 0);
  doc.line(mm(8), y, pageWidth - mm(8), y);
  doc.setLineDash([]);
  y += mm(13);
  y = checkPageBreak(y);

  // --- THANK YOU & BRAND ---
  doc.setFont("Inter-Regular", "normal").setFontSize(8);
  doc.text("Thank you!", pageWidth / 2, y, { align: "center" });
  y += mm(12);
  doc.text("** Visit Again **", pageWidth / 2, y, { align: "center" });
  if (storeSettings?.negobillBranding) {
    y += mm(12);
    doc.text("Powered by", pageWidth / 2, y, { align: "center" });
    y += mm(12);

    // --- Website (bold NegoBill) ---
    const sitePrefix = "www.";
    const siteBold = "NegoBill";
    const siteSuffix = ".com";
    const prefixWidth = doc.getTextWidth(sitePrefix);
    const boldWidth = doc.getTextWidth(siteBold);
    const suffixWidth = doc.getTextWidth(siteSuffix);
    const totalWidth = prefixWidth + boldWidth + suffixWidth;
    const startX = (pageWidth - totalWidth) / 2;

    doc.setFont("Inter-Regular", "normal");
    doc.text(sitePrefix, startX, y);
    doc.setFont("Inter-Bold", "bold");
    doc.text(siteBold, startX + prefixWidth, y);
    doc.setFont("Inter-Regular", "normal");
    doc.text(siteSuffix, startX + prefixWidth + boldWidth, y);
  }
  // setPrint(false);
  return doc;
};

export const buildReceiptCommands3InchBill = (data) => {
  const commands = [];

  const saleDate = data?.saledate;
  const timezone = data?.saleby?.timezone;

  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }
  const { date, time } = saleTime;
  // 3-inch paper = 48 chars wide (NOT 32!)
  // Most 3-inch/80mm printers print 48 chars at normal font
  const W = 48;

  const LINE = "-".repeat(W);

  const pad = (left, right, width = W) => {
    const gap = width - left.length - right.length;
    return left + " ".repeat(Math.max(1, gap)) + right;
  };

  const center = (str, width = W) => {
    const spaces = Math.max(0, Math.floor((width - str.length) / 2));
    return " ".repeat(spaces) + str;
  };

  const fmt = (num) => parseFloat(num || 0).toFixed(2); // always 2 decimal places

  // ─── STORE NAME ─────────────────────────────────────────────

  if (data?.imagePath) {
    commands.unshift({
      // unshift = add to beginning
      type: "image",
      value: data?.imagePath, // base64 image string from your store data
    });
  }
  commands.push({ type: "bold", value: true });
  commands.push({ type: "size", width: 1, height: 0 });
  commands.push({
    type: "text",
    value: `${data?.status === "CANCELLED" ? "CANCELLED BILL RECEIPT" : "BILL RECEIPT"}`,
  });
  commands.push({ type: "size", width: 0, height: 0 });
  commands.push({ type: "text", value: data?.salefrom?.title || "STORE NAME" });
  commands.push({ type: "bold", value: false });
  commands.push({ type: "text", value: `Ph:${data.salefrom.phone}` });

  // ─── STORE ADDRESS ──────────────────────────────────────────
  const s = data?.salefrom;
  if (s?.addressline1) commands.push({ type: "text", value: s?.addressline1 });

  const addrLine2 = [s?.district, s?.state].filter(Boolean).join(", ");
  if (addrLine2) commands.push({ type: "text", value: addrLine2 });

  // PAN + FSSAI on same line
  const panFssai = [
    s.email ? `Email : ${s.email}` : null,
    s?.pannumber ? `PAN  : ${s?.pannumber}` : null,
    s?.fssai ? `FSSAI:${s?.fssai}` : null,
  ]
    .filter(Boolean)
    .join("   ");
  if (panFssai) commands.push({ type: "text", value: panFssai });

  // ─── BILL INVOICE ───────────────────────────────────────────
  commands.push({ type: "text", value: LINE });

  // ─── INVOICE META ───────────────────────────────────────────
  commands.push({ type: "align", value: "LT" });
  commands.push({ type: "text", value: `BILL No  : ${data?.saleno}` });
  commands.push({ type: "text", value: `Date     : ${date}, ${time}` });
  commands.push({ type: "bold", value: true });
  commands.push({ type: "text", value: `Amount   : Rs.${data.grandtotal}` });
  commands.push({ type: "bold", value: false });

  // ─── BUYER INFO ─────────────────────────────────────────────
  const buyer = data?.saleto;
  const hasBuyer = buyer && (buyer?.name || buyer?.phone);
  // && optData?.isCustomerDisplay
  if (hasBuyer) {
    commands.push({ type: "text", value: LINE });
    commands.push({ type: "align", value: "CT" });
    commands.push({ type: "text", value: "BUYER  INFORMATION" });
    commands.push({ type: "align", value: "LT" });
    if (buyer.name) {
      commands.push({ type: "bold", value: true });
      commands.push({ type: "text", value: buyer?.name });
      commands.push({ type: "bold", value: false });
    }
    if (buyer.phone)
      commands.push({ type: "text", value: `Ph: ${buyer.phone}` });
    if (buyer.addressline1)
      commands.push({ type: "text", value: `${buyer.addressline1}` });
    commands.push({
      type: "text",
      value: `${buyer.state && buyer?.state}, ${buyer?.district && buyer?.district}, ${buyer?.country && buyer?.country}`,
    });
  }
  // if (buyer?.email)    commands.push({ type: "text", value: `Email: ${buyer?.email}` });
  if (buyer?.pannumber)
    commands.push({ type: "text", value: `PAN: ${buyer?.pannumber}` });
  if (buyer?.cinnumber)
    commands.push({ type: "text", value: `CIN: ${buyer?.cinnumber}` });
  if (buyer?.fssai)
    commands.push({ type: "text", value: `FSSI: ${buyer?.fssai}` });
  if (buyer?.referenceid)
    commands.push({
      type: "text",
      value: `Reference No: ${buyer?.referenceid}`,
    });

  // ─── ITEMS TABLE HEADER ─────────────────────────────────────
  // Layout (48 chars): Name(left) | Rate(col) | Qty(col) | Total(col)
  // Col widths:  Name=20  Rate=9  Qty=5  Total=9  (but name is on its own line)
  // Sub-header row: "        Rate     Qty   Total"
  commands.push({ type: "text", value: LINE });
  commands.push({ type: "align", value: "LT" });
  commands.push({ type: "bold", value: true });
  commands.push({ type: "text", value: "Items" });

  // W = 48, split into 3 equal columns of 16 each
  // padStart = right-align text WITHIN that column width
  const rateCol = "Rate".padStart(16); // 16 chars, text on RIGHT
  const qtyCol = "Qty".padStart(16); // 16 chars, text on RIGHT
  const totalCol = "Total".padStart(16); // 16 chars, text on RIGHT
  commands.push({ type: "text", value: rateCol + qtyCol + totalCol });
  commands.push({ type: "bold", value: false });
  commands.push({ type: "text", value: LINE });

  // ─── ITEMS ROWS ──────────────────────────────────────────────
  data.saleitems?.forEach((item) => {
    // Line 1: item name
    const itemName = item.title + (item.note ? ` (${item.note})` : "");
    commands.push({ type: "text", value: itemName });

    // Line 2: rate | qty | total — each right-aligned in 16 char column
    const rate = `Rs.${item.soldprice}`;
    const qty = `${item?.quantity} ${(() => {
      const unitCode = item?.isSecondaryUnit
        ? item?.unitConfig?.secondaryUnitCode
        : item?.unitConfig?.primaryUnitCode;
      return unitCode || "";
    })()}`;
    const total = `Rs.${item.lineTotalSoldPrice}`;

    commands.push({
      type: "text",
      value: rate.padStart(18) + qty.padStart(14) + total.padStart(16),
    });
  });

  commands.push({ type: "text", value: LINE });

  // ─── ITEMS COUNT ────────────────────────────────────────────
  commands.push({ type: "text", value: `Total Items : ${data.totalItems}` });

  // ─── GRAND TOTAL (right aligned, big) ───────────────────────
  commands.push({ type: "bold", value: true });
  commands.push({ type: "text", value: `Grand Total Rs.${data.grandtotal}` });
  commands.push({ type: "bold", value: false });

  // ─── PAYMENT MODE ───────────────────────────────────────────

  if (parseFloat(data.totaldiscount) > 0) {
    commands.push({ type: "text", value: LINE });
    commands.push({ type: "align", value: "CT" });
    commands.push({
      type: "text",
      value: `YOU SAVED : Rs.${data.totaldiscount}`,
    });
  }

  commands.push({ type: "text", value: LINE });
  commands.push({ type: "align", value: "CT" });
  commands.push({ type: "bold", value: true });
  commands.push({ type: "text", value: "PAYMENT  MODE" });
  commands.push({ type: "bold", value: false });
  commands.push({ type: "align", value: "LT" });
  commands.push({ type: "text", value: LINE });

  const payments = [
    { label: "Cash", amount: data.cashPaid },
    { label: "Card", amount: data.cardPaid },
    { label: "Online Pay", amount: data.onlinePaid },
    { label: "UPI", amount: data.upiPaid },
    { label: "Balance Due", amount: data.balanceDue },
  ].filter((p) => p?.amount > 0);

  payments.forEach((p) => {
    commands.push({ type: "text", value: pad(p?.label, `Rs.${p?.amount}`) });
  });
  // ─── YOU SAVED ──────────────────────────────────────────────
  // if (parseFloat(data.totaldiscount) > 0) {
  //   commands.push({ type: "text", value: LINE });
  //   commands.push({ type: "align", value: "CT" });
  //   commands.push({ type: "text", value: `YOU SAVED : Rs.${(data.totaldiscount)}` });
  // }

  // ─── FOOTER ─────────────────────────────────────────────────
  commands.push({ type: "text", value: LINE });
  commands.push({ type: "align", value: "CT" });
  commands.push({ type: "text", value: "Thank you for your purchase!" });
  commands.push({ type: "text", value: "** Visit us Again **" });
  commands.push({ type: "text", value: "Powered By" });
  if (data.negobillBranding) {
    commands.push({ type: "bold", value: true });
    commands.push({ type: "text", value: "www.NegoBill.com" });
    commands.push({ type: "bold", value: false });
  }

  commands.push({ type: "feed", value: 1 });
  commands.push({ type: "cut" });

  return commands;
};

export const buildReceiptCommands3InchTax = (data) => {
  const commands = [];

  const saleDate = data?.saledate;
  const timezone = data?.saleby?.timezone;

  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }
  const { date, time } = saleTime;
  // 3-inch paper = 48 chars wide (NOT 32!)
  // Most 3-inch/80mm printers print 48 chars at normal font
  const W = 48;

  const LINE = "-".repeat(W);

  const pad = (left, right, width = W) => {
    const gap = width - left.length - right.length;
    return left + " ".repeat(Math.max(1, gap)) + right;
  };

  const center = (str, width = W) => {
    const spaces = Math.max(0, Math.floor((width - str.length) / 2));
    return " ".repeat(spaces) + str;
  };

  const fmt = (num) => parseFloat(num || 0).toFixed(2); // always 2 decimal places

  // ─── STORE NAME ─────────────────────────────────────────────
  commands.push({ type: "align", value: "CT" });
  if (data?.imagePath) {
    commands.push({
      // unshift = add to beginning
      type: "image",
      value: data?.imagePath, // base64 image string from your store data
    });
  }

  commands.push({ type: "align", value: "CT" });
  commands.push({ type: "bold", value: true });
  commands.push({ type: "size", width: 1, height: 0 });
  commands.push({
    type: "text",
    value: `${data?.status === "CANCELLED" ? "CANCELLED TAX INVOICE" : "TAX INVOICE"}`,
  });
  commands.push({ type: "size", width: 0, height: 0 });
  commands.push({ type: "text", value: data?.salefrom?.title || "STORE NAME" });
  commands.push({ type: "bold", value: false });
  commands.push({ type: "text", value: `Ph:${data.salefrom.phone}` });

  // ─── STORE ADDRESS ──────────────────────────────────────────
  const s = data?.salefrom;
  if (s?.addressline1) commands.push({ type: "text", value: s?.addressline1 });

  const addrLine2 = [s?.district, s?.state].filter(Boolean).join(", ");
  if (addrLine2) commands.push({ type: "text", value: addrLine2 });

  // PAN + FSSAI on same line
  const panFssai = [
    s.email ? `Email:${s.email}` : null,
    s?.pannumber ? `PAN:${s?.pannumber}` : null,
    s?.fssai ? `FSSAI:${s?.fssai}` : null,
  ]
    .filter(Boolean)
    .join("   ");
  if (panFssai) commands.push({ type: "text", value: panFssai });

  if (s?.gstnumber)
    commands.push({ type: "text", value: `GSTIN:${s?.gstnumber}` });

  // ─── BILL INVOICE ───────────────────────────────────────────
  commands.push({ type: "text", value: LINE });
  // commands.push({ type: "align", value: "CT" });
  // commands.push({ type: "bold", value: true });
  // commands.push({ type: "size", width: 1, height: 0 });
  // commands.push({ type: "text", value: `${data?.status === "CANCELLED" ? "CANCELLED TAX INVOICE" : "TAX INVOICE"}` });
  // commands.push({ type: "size", width: 0, height: 0 });
  // commands.push({ type: "bold", value: false });
  // commands.push({ type: "text", value: LINE });

  // ─── INVOICE META ───────────────────────────────────────────
  commands.push({ type: "align", value: "LT" });
  commands.push({ type: "text", value: `Invoice No : ${data?.saleno}` });
  commands.push({ type: "text", value: `Date       : ${date}, ${time}` });
  commands.push({ type: "bold", value: true });
  commands.push({ type: "text", value: `Amount     : Rs.${data.grandtotal}` });
  commands.push({ type: "bold", value: false });

  // ─── BUYER INFO ─────────────────────────────────────────────
  const buyer = data?.saleto;
  const hasBuyer = buyer && (buyer?.name || buyer?.phone);
  // data.storeSettings?.saleSettings?.isCustomerDisplay
  if (hasBuyer) {
    commands.push({ type: "text", value: LINE });
    commands.push({ type: "align", value: "CT" });
    commands.push({ type: "text", value: "BUYER  INFORMATION" });
    commands.push({ type: "align", value: "LT" });
    if (buyer.name) {
      commands.push({ type: "bold", value: true });
      commands.push({ type: "text", value: buyer?.name });
      commands.push({ type: "bold", value: false });
    }
    if (buyer.phone)
      commands.push({ type: "text", value: `Ph: ${buyer.phone}` });
    if (buyer.addressline1)
      commands.push({ type: "text", value: `${buyer.addressline1}` });
    commands.push({
      type: "text",
      value: `${buyer?.district && buyer?.district}, ${buyer.state && buyer?.state}, ${buyer?.country && buyer?.country}`,
    });

    if (buyer.email)
      commands.push({ type: "text", value: `Email: ${buyer.email}` });
    if (buyer.gstnumber)
      commands.push({ type: "text", value: `GST: ${buyer.gstnumber}` });
    if (buyer.pannumber)
      commands.push({ type: "text", value: `PAN: ${buyer.pannumber}` });
    if (buyer.cinnumber)
      commands.push({ type: "text", value: `CIN: ${buyer.cinnumber}` });
    if (buyer?.fssai)
      commands.push({ type: "text", value: `FSSI: ${buyer?.fssai}` });
    if (buyer?.referenceid)
      commands.push({
        type: "text",
        value: `Reference No: ${buyer?.referenceid}`,
      });
  }

  // ─── ITEMS TABLE HEADER ─────────────────────────────────────
  // Layout (48 chars): Name(left) | Rate(col) | Qty(col) | Total(col)
  // Col widths:  Name=20  Rate=9  Qty=5  Total=9  (but name is on its own line)
  // Sub-header row: "        Rate     Qty   Total"
  commands.push({ type: "text", value: LINE });
  commands.push({ type: "align", value: "LT" });
  commands.push({ type: "bold", value: true });
  commands.push({ type: "text", value: "Items" });

  // W = 48, split into 3 equal columns of 16 each
  // padStart = right-align text WITHIN that column width
  const rateCol = "Rate".padStart(16); // 16 chars, text on RIGHT
  const qtyCol = "Qty".padStart(16); // 16 chars, text on RIGHT
  const totalCol = "Total".padStart(16); // 16 chars, text on RIGHT
  commands.push({ type: "text", value: rateCol + qtyCol + totalCol });
  commands.push({ type: "bold", value: false });
  commands.push({ type: "text", value: LINE });

  // ─── ITEMS ROWS ──────────────────────────────────────────────
  data.saleitems?.forEach((item) => {
    // Line 1: item name
    const itemName = item.title + (item.note ? ` (${item.note})` : "");
    commands.push({ type: "text", value: itemName });

    // Line 2: rate | qty | total — each right-aligned in 16 char column
    const rate = `Rs.${item.soldprice}`;
    const qty = `${item?.quantity} ${(() => {
      const unitCode = item?.isSecondaryUnit
        ? item?.unitConfig?.secondaryUnitCode
        : item?.unitConfig?.primaryUnitCode;
      return unitCode || "";
    })()}`;
    const total = `Rs.${item.lineTotalSoldPrice}`;

    commands.push({
      type: "text",
      value:
        rate.padStart(18) + // col1 — right aligned in 16 chars
        qty.padStart(14) + // col2 — right aligned in 16 chars
        total.padStart(16), // col3 — right aligned in 16 chars
    });
  });

  commands.push({ type: "text", value: LINE });

  // ─── ITEMS COUNT ────────────────────────────────────────────
  commands.push({ type: "text", value: `Total Items : ${data.totalItems}` });

  // ─── GRAND TOTAL (right aligned, big) ───────────────────────
  commands.push({ type: "bold", value: true });
  commands.push({ type: "text", value: `Grand Total Rs.${data.grandtotal}` });
  commands.push({ type: "bold", value: false });

  // ─── PAYMENT MODE ───────────────────────────────────────────

  if (parseFloat(data.totaldiscount) > 0) {
    commands.push({ type: "text", value: LINE });
    commands.push({ type: "align", value: "CT" });
    commands.push({
      type: "text",
      value: `YOU SAVED : Rs.${data.totaldiscount}`,
    });
  }

  // commands.push({ type: "text", value: LINE });
  // commands.push({ type: "bold", value: true });
  // commands.push({ type: "text", value: pad("Grand Total:", `Rs${(data?.grandtotal)}`) });
  // commands.push({ type: "bold", value: false });
  // ─── TAX TABLE ──────────────────────────────────────────────
  const isIGST =
    data?.salefrom?.statecode &&
    data?.saleto?.statecode &&
    data?.salefrom.statecode !== data?.saleto.statecode;

  const taxRows = data?.saleitems?.reduce((acc, product) => {
    const pct = product.taxpercentage || 0;
    const tax = product.lineTotalTaxAmount || 0;
    if (!acc[pct]) acc[pct] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    acc[pct].basePrice += product.lineTotalBasePrice || 0;
    if (isIGST) acc[pct].igst += tax;
    else {
      acc[pct].sgst += tax / 2;
      acc[pct].cgst += tax / 2;
    }
    return acc;
  }, {});

  const hasTax = Object.entries(taxRows || {}).some(([p]) => parseFloat(p) > 0);
  if (hasTax) {
    commands.push({ type: "text", value: LINE });
    commands.push({ type: "bold", value: true });

    if (isIGST) {
      // Header — 3 columns
      commands.push({
        type: "text",
        value:
          "Tax%".padEnd(10) + // col1 — 10 chars
          "Amount".padEnd(20) + // col2 — 20 chars
          "IGST", // col3 — right end
      });
      commands.push({ type: "bold", value: false });

      Object.entries(taxRows)
        .filter(([p]) => parseFloat(p) > 0)
        .forEach(([pct, t]) => {
          commands.push({
            type: "text",
            value:
              `${pct}%`.padEnd(10) + // col1 — Tax%
              `Rs.${t.basePrice}`.padEnd(20) + // col2 — Amount
              `Rs.${t.igst}`, // col3 — IGST
          });
        });
    } else {
      // Header — 4 columns
      commands.push({
        type: "text",
        value:
          "Tax%".padEnd(10) + // col1 — 10 chars
          "Amount".padEnd(14) + // col2 — 14 chars
          "SGST".padEnd(12) + // col3 — 12 chars
          "CGST", // col4 — right end
      });
      commands.push({ type: "bold", value: false });

      Object.entries(taxRows)
        .filter(([p]) => parseFloat(p) > 0)
        .forEach(([pct, t]) => {
          commands.push({
            type: "text",
            value:
              `${pct}%`.padEnd(10) + // col1 — Tax%
              `Rs.${t.basePrice}`.padEnd(14) + // col2 — Amount
              `Rs.${t.sgst}`.padEnd(12) + // col3 — SGST
              `Rs.${t.cgst}`, // col4 — CGST
          });
        });
    }
    commands.push({ type: "text", value: LINE });
    commands.push({ type: "bold", value: true });
    commands.push({
      type: "text",
      value: pad("Total Tax:", `Rs.${data?.totaltaxamount}`),
    });
    commands.push({ type: "bold", value: false });
  }
  commands.push({ type: "text", value: LINE });
  commands.push({ type: "align", value: "CT" });
  commands.push({ type: "bold", value: true });
  commands.push({ type: "text", value: "PAYMENT  MODE" });
  commands.push({ type: "bold", value: false });
  commands.push({ type: "align", value: "LT" });
  commands.push({ type: "text", value: LINE });

  const payments = [
    { label: "Cash", amount: data.cashPaid },
    { label: "Card", amount: data.cardPaid },
    { label: "Online Pay", amount: data.onlinePaid },
    { label: "UPI", amount: data.upiPaid },
    { label: "Balance Due", amount: data.balanceDue },
  ].filter((p) => p?.amount > 0);

  payments.forEach((p) => {
    commands.push({ type: "text", value: pad(p?.label, `Rs.${p?.amount}`) });
  });
  // ─── YOU SAVED ──────────────────────────────────────────────
  // if (parseFloat(data.totaldiscount) > 0) {
  //   commands.push({ type: "text", value: LINE });
  //   commands.push({ type: "align", value: "CT" });
  //   commands.push({ type: "text", value: `YOU SAVED : Rs.${(data.totaldiscount)}` });
  // }

  // ─── FOOTER ─────────────────────────────────────────────────
  commands.push({ type: "text", value: LINE });
  commands.push({ type: "align", value: "CT" });
  commands.push({ type: "text", value: "Thank you for your purchase!" });
  commands.push({ type: "text", value: "** Visit us Again **" });
  commands.push({ type: "text", value: "Powered By" });
  if (data.negobillBranding) {
    commands.push({ type: "bold", value: true });
    commands.push({ type: "text", value: "www.NegoBill.com" });
    commands.push({ type: "bold", value: false });
  }

  commands.push({ type: "feed", value: 1 });
  commands.push({ type: "cut" });

  return commands;
};
